import { SongData, WordData, WordState, SongState } from '../types'
import { gospelTerms } from '../utils/gospelTerms'
import { EmojiDictionary } from './EmojiDictionary'

export class SongManager {
	private static readonly STORAGE_PREFIX = 'lyrics-memorization-song.'

	// Process lyrics with emoji insertion
	static processLyricsWithEmojis(lyrics: string): string {
		const words = lyrics.split(/(\s+)/)
		const processedWords = words.map(word => {
			// Skip whitespace and punctuation-only words
			if (/^\s+$/.test(word) || !/[a-zA-Z]/.test(word)) return word

			// Skip words that already have annotations (including empty parentheses)
			if (/\(.*\)/.test(word)) return word

			// Extract the actual word without punctuation
			const match = word.match(/^([^\w]*)(.*?)([^\w]*)$/)
			if (!match) return word

			const [, prefix, cleanWord, suffix] = match
			const emoji = EmojiDictionary.getEmojiForWord(cleanWord)

			if (emoji) {
				return `${prefix}${cleanWord}${suffix}(${emoji})`
			}
			return word
		})

		return processedWords.join('')
	}

	static parseLyrics(lyrics: string): WordData[][][] {
		const allPages: WordData[][][] = []
		let wordIndex = 0

		const pagesText = lyrics.split(/(?:\n\s*)+\n/) // Split by two or more newlines for page breaks

		pagesText.forEach(pageText => {
			const currentPage: WordData[][] = []
			const lines = pageText.split('\n').filter(line => line.trim().length > 0)

			lines.forEach(line => {
				const currentLine: WordData[] = []
				const wordsInLine = line.trim().split(/\s+/).filter(word => word.length > 0)

				if (wordsInLine.length === 0) {
					return
				}

				wordsInLine.forEach((word) => {
					const symbolMatch = word.match(/^(.*?)\((.+?)\)(.*?)$/)
					let wordData: WordData

					if (symbolMatch) {
						let [, wordText, symbolContent, punctuation] = symbolMatch
						// re-insert punctuation after the symbol
						wordText = wordText + punctuation
						if (symbolContent.startsWith('!')) {
							const term = symbolContent.substring(1)
							const gospelTerm = gospelTerms[term]
							if (gospelTerm) {
								wordData = {
									id: `word-${wordIndex}`,
									text: wordText || gospelTerm.label,
									symbol: {
										type: 'gospel',
										src: gospelTerm.src,
										// srcset: gospelTerm.srcset,
										// sizes: gospelTerm.sizes,
									},
								}
							} else {
								wordData = {
									id: `word-${wordIndex}`,
									text: wordText || word,
									symbol: {
										type: 'emoji',
										symbol: symbolContent,
									},
								}
							}
						} else {
							wordData = {
								id: `word-${wordIndex}`,
								text: wordText,
								symbol: {
									type: 'emoji',
									symbol: symbolContent,
								},
							}
						}
					} else {
						wordData = {
							id: `word-${wordIndex}`,
							text: word,
						}
					}
					currentLine.push(wordData)
					wordIndex++
				})
				currentPage.push(currentLine)
			})
			allPages.push(currentPage)
		})

		return allPages
	}

	// Convert persistent WordData to runtime WordState with default visibility
	static createWordStates(wordData: WordData[]): WordState[] {
		return wordData.map(word => ({
			...word,
			isVisible: true,
			firstLetter: false,
		}))
	}

	// Convert SongData to SongState for runtime use
	static createSongState(songData: SongData): SongState {
		const parsedPages = SongManager.parseLyrics(songData.lyrics)

		const pagesWithState = parsedPages.map(page =>
			page.map(line => {
				const lineWithState = line.map((word, index) => {
					const isPlain = index === 0 && /^\d+\.$/.test(word.text)
					return { ...word, isVisible: true, firstLetter: false, isPlain }
				})
				return lineWithState
			}),
		)

		return {
			...songData,
			pages: pagesWithState,
		}
	}

	static saveSong(songData: SongData): void {
		// Add lastModified timestamp to the song data
		const songWithTimestamp: SongData = {
			id: songData.id,
			title: songData.title,
			lyrics: songData.lyrics,
			lastModified: Date.now(),
		}

		localStorage.setItem(`${this.STORAGE_PREFIX}${songData.id}`, JSON.stringify(songWithTimestamp))
	}

	static loadSong(songId: string): SongData | null {
		const stored = localStorage.getItem(`${this.STORAGE_PREFIX}${songId}`)
		if (!stored) return null

		try {
			return JSON.parse(stored)
		} catch {
			return null
		}
	}

	static getSongList(): Array<{ id: string; title: string; lastModified: number }> {
		const songs: Array<{ id: string; title: string; lastModified: number }> = []

		// Iterate through all localStorage keys
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i)
			if (key && key.startsWith(this.STORAGE_PREFIX)) {
				try {
					const songData = JSON.parse(localStorage.getItem(key) || '')
					if (songData && songData.id && songData.title) {
						songs.push({
							id: songData.id,
							title: songData.title,
							lastModified: songData.lastModified || 0,
						})
					}
				} catch {
					// Skip invalid entries
					continue
				}
			}
		}

		return songs
	}

	static deleteSong(songId: string): void {
		localStorage.removeItem(`${this.STORAGE_PREFIX}${songId}`)
	}

	static createSongFromLyrics(lyrics: string, title: string = 'Untitled Song'): SongData {
		return {
			id: this.generateSongId(),
			title,
			lyrics,
		}
	}

	static generateSongId(): string {
		return `${Date.now()}`
	}
}
